/**
 * Cache em memória: blob + um único object URL por produto (Display / TV).
 * - Evita GET repetido na API ao rotacionar.
 * - Reutiliza o mesmo `blob:` no `<img>`, para o DevTools não acumular URLs novos a cada volta.
 */

const MAX_ENTRIES = 96;

type Entry = { blob: Blob; objectUrl: string };

const entryByProdutoId = new Map<number, Entry>();

function touchLru(produtoId: number): void {
  const entry = entryByProdutoId.get(produtoId);
  if (!entry) return;
  entryByProdutoId.delete(produtoId);
  entryByProdutoId.set(produtoId, entry);
}

function revokeAndDelete(produtoId: number): void {
  const entry = entryByProdutoId.get(produtoId);
  if (entry?.objectUrl) {
    URL.revokeObjectURL(entry.objectUrl);
  }
  entryByProdutoId.delete(produtoId);
}

function evictOldestWhileOverLimit(): void {
  while (entryByProdutoId.size > MAX_ENTRIES) {
    const oldest = entryByProdutoId.keys().next().value as number | undefined;
    if (oldest === undefined) break;
    revokeAndDelete(oldest);
  }
}

/**
 * URL estável para o `<img>` se o produto já estiver em cache (sem novo createObjectURL).
 */
export function getPanfletoFotoDisplayUrlFromCache(produtoId: number): string | undefined {
  if (!produtoId || produtoId <= 0) return undefined;
  const entry = entryByProdutoId.get(produtoId);
  if (!entry?.blob || entry.blob.size === 0) {
    if (entry) revokeAndDelete(produtoId);
    return undefined;
  }
  touchLru(produtoId);
  return entry.objectUrl;
}

/**
 * Guarda o blob após download; devolve o object URL (criado uma vez por produto até LRU/clear).
 */
export function putPanfletoFotoBlobInCache(produtoId: number, blob: Blob): string | undefined {
  if (!produtoId || produtoId <= 0 || !blob || blob.size === 0) return undefined;
  const prev = entryByProdutoId.get(produtoId);
  if (prev?.objectUrl) {
    URL.revokeObjectURL(prev.objectUrl);
  }
  entryByProdutoId.delete(produtoId);
  const objectUrl = URL.createObjectURL(blob);
  entryByProdutoId.set(produtoId, { blob, objectUrl });
  evictOldestWhileOverLimit();
  return objectUrl;
}

/** Liberta RAM ao sair do Display / TV. */
export function clearPanfletoFotoBlobCache(): void {
  entryByProdutoId.forEach((entry) => {
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
  });
  entryByProdutoId.clear();
}
